import { z } from '@hono/zod-openapi';
import { PASSWORD_MIN_LENGTH, passwordRegex } from './utils';
import { isValidNumber } from 'libphonenumber-js';

export const logInOTPBodySchema = z.object({
  phone: z
    .string()
    .min(1, {
      message: 'Phone number is required'
    })
    .openapi({ description: 'User phone number' })
});

export const verifyOTPBodySchema = z.object({
  phone: z.string().openapi({ description: 'User phone number' }),
  otp: z
    .string()
    .min(1, {
      message: 'OTP is required'
    })
    .openapi({ description: 'One-time password code sent to user phone' })
});

export const emailSchema = z
  .string()
  .min(1, {
    message: 'Email or phone number is required'
  })
  .email({ message: 'Invalid email or phone number' });
export const phoneSchema = z
  .string()
  .min(1, {
    message: 'Email or phone number is required'
  })
  .refine(
    (value) => {
      return isValidNumber(value);
    },
    {
      message: 'Invalid email or phone number'
    }
  );

export const signInRequestSchema = z.object({
  phoneOrEmail: emailSchema.or(phoneSchema).openapi({
    description: 'User phone number or email'
  }),
  intent: z.enum(['volunteer', 'partner', 'admin']).optional().openapi({
    description:
      'Optional sign-in intent. Use partner to require a partner account. Use admin to require an admin account.'
  }),
  password: z
    .string()
    .min(1, {
      message: 'Password is required'
    })
    .openapi({ description: 'User password' })
});

export const signInResponseSchema = z.object({
  data: z.object({
    tokens: z.object({
      accessToken: z.string().openapi({ description: 'JWT Access Token' }),
      refreshToken: z.string().openapi({ description: 'JWT Refresh Token' }),
      idToken: z.string().openapi({ description: 'JWT ID Token' })
    })
  })
});

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, {
    message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`
  })
  .regex(passwordRegex.noWhitespace, {
    message: 'Password must not contain whitespace.'
  })
  .regex(passwordRegex.uppercase, {
    message: 'Password must contain at least one uppercase letter'
  })
  .regex(passwordRegex.lowercase, {
    message: 'Password must contain at least one lowercase letter'
  })
  .regex(passwordRegex.number, {
    message: 'Password must contain at least one number'
  })
  .regex(passwordRegex.specialChar, {
    message:
      'Password must contain at least one special character (e.g., !@#$%^&*)'
  });

export const signUpRequestSchema = z.object({
  profile: z.object({
    email: z
      .string()
      .min(1, {
        message: 'Email is required'
      })
      .email({
        message: 'Invalid email address'
      })
      .openapi({ description: 'User email address' }),
    firstName: z
      .string()
      .min(1, {
        message: 'First name is required'
      })
      .openapi({ description: 'First name of the user' }),
    lastName: z
      .string()
      .min(1, {
        message: 'Last name is required'
      })
      .openapi({ description: 'Last name of the user' })
  }),
  phone: z
    .string()
    .min(1, {
      message: 'Phone number is required'
    })
    .openapi({
      description:
        'Unique identifier for the user, in this case, the phone number'
    }),
  password: passwordSchema.openapi({ description: 'User password' })
});

export const forgotBodySchema = z.object({
  email: z.string().email().openapi({ description: 'User email address' }),
  intent: z.enum(['volunteer', 'partner', 'admin']).optional().openapi({
    description:
      'Intent of the forgot password request. If partner, user must have partner role. If admin, user must have admin role.'
  })
});

export const forgotResponseSchema = z.object({
  message: z
    .string()
    .openapi({ description: 'Forgot password confirmation message' })
});

export const resetParamSchema = z.object({
  email: z.string().email().openapi({ description: 'User email address' }),
  token: z.string().openapi({ description: 'Password reset token' })
});

export const resetBodySchema = z.object({
  password: passwordSchema.openapi({ description: 'New password' })
});

export const resetResponseSchema = z.object({
  message: z
    .string()
    .openapi({ description: 'Password reset confirmation message' })
});

export const signUpResponseSchema = z.object({});

export type SignUpRequest = z.infer<typeof signUpRequestSchema>;
